import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {HTMLElement | null} */
  #portalContainer = null;
  /** @type {HTMLElement | null} */
  #originalParent = null;
  /** @type {HTMLElement | null} */
  #originalNextSibling = null;
  /** @type {HTMLDivElement | null} Cached ref — the framework loses track when portaled */
  #menuDrawerEl = null;
  /** @type {HTMLDivElement | null} */
  #backdropEl = null;
  /** @type {((e: Event) => void) | null} */
  #portalEventHandler = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#destroyPortal();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Portal the drawer + backdrop to body so they escape any sticky stacking context
    this.#portalToBody();

    const menuDrawer = this.#menuDrawerEl;
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      // When portaled, the drawer is no longer a child of .menu-open,
      // so we add the active class directly to trigger CSS visibility
      menuDrawer?.classList.add('menu-drawer--portaled-open');
      this.#backdropEl?.classList.add('menu-drawer__backdrop--portaled-open');

      if (target) {
        menuDrawer?.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus.
      // When portaled, the focusable elements live in the portal container,
      // so we must trap focus there instead of in the (now-empty) details.
      const focusTarget = this.#portalContainer || details;
      onAnimationEnd(menuDrawer || details, () => trapFocus(focusTarget), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const menuDrawer = this.#menuDrawerEl || this.refs.menuDrawer;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    menuDrawer?.classList.remove('menu-drawer--has-submenu-opened');

    // Remove portaled-open classes to trigger close animation
    menuDrawer?.classList.remove('menu-drawer--portaled-open');
    this.#backdropEl?.classList.remove('menu-drawer__backdrop--portaled-open');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = menuDrawer || details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          // Return the drawer from the portal back to its original position
          this.#returnFromPortal();
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Move the menu-drawer and backdrop out of the header stacking context
   * into a portal container on document.body
   */
  #portalToBody() {
    const menuDrawer = this.refs.menuDrawer;
    const backdrop = this.refs.details.querySelector('.menu-drawer__backdrop');
    if (!menuDrawer) return;

    // Cache direct references — the framework's ref system loses track
    // of elements once they leave the component's DOM subtree
    this.#menuDrawerEl = menuDrawer;
    this.#backdropEl = backdrop;

    // Remember where elements came from.
    // The backdrop (if present) is the next sibling of the drawer and will also
    // be portaled, so the reinsertion reference must be the element AFTER the
    // last portaled node — otherwise insertBefore() throws because the
    // reference node was also moved out of the parent.
    this.#originalParent = menuDrawer.parentElement;
    this.#originalNextSibling = backdrop
      ? backdrop.nextElementSibling
      : menuDrawer.nextElementSibling;

    // Create portal container
    if (!this.#portalContainer) {
      this.#portalContainer = document.createElement('div');
      this.#portalContainer.className = 'header-drawer-portal';
      this.#portalContainer.style.cssText = 'position:fixed;inset:0;z-index:var(--layer-menu-drawer, 18);pointer-events:none;';
    }

    document.body.appendChild(this.#portalContainer);
    this.#portalContainer.appendChild(menuDrawer);
    if (backdrop) {
      this.#portalContainer.appendChild(backdrop);
    }

    // Enable pointer events on children
    menuDrawer.style.pointerEvents = 'auto';
    if (backdrop) backdrop.style.pointerEvents = 'auto';

    // Delegated event handler: on:click attributes use .closest('header-drawer')
    // which fails when elements are outside the component. This handler intercepts
    // clicks inside the portal and routes them back to this component.
    this.#portalEventHandler = (event) => {
      const el = event.target instanceof Element
        ? (event.target.hasAttribute('on:click') ? event.target : event.target.closest('[on\\:click]'))
        : null;
      if (!el) return;

      const value = el.getAttribute('on:click') ?? '';
      const [, method] = value.split('/');
      if (!method) return;

      const cleanMethod = method.replace(/\?.*/, '');
      const callback = /** @type {any} */ (this)[cleanMethod];
      if (typeof callback !== 'function') return;

      // Extract data argument (e.g., 'submenu' from 'header-drawer/open/submenu')
      const matches = value.match(/([/\?][^/\?]+)([/\?][^/\?]+)$/);
      const args = [event];
      if (matches?.[2]) {
        const data = matches[2].slice(1); // strip leading / or ?
        args.unshift(data);
      }
      callback.call(this, ...args);

      // Stop propagation so the framework's handler (which would fail to
      // resolve the component) doesn't log errors
      event.stopPropagation();
    };
    this.#portalContainer.addEventListener('click', this.#portalEventHandler);

    // Escape key events from inside the portal won't bubble to the header-drawer
    // element, so listen directly on the portal container
    this.#portalContainer.addEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Return the drawer elements back into the original details element
   */
  #returnFromPortal() {
    const menuDrawer = this.#menuDrawerEl;
    const backdrop = this.#backdropEl;

    if (this.#originalParent && menuDrawer) {
      // Insert drawer back in original position
      if (this.#originalNextSibling) {
        this.#originalParent.insertBefore(menuDrawer, this.#originalNextSibling);
      } else {
        this.#originalParent.appendChild(menuDrawer);
      }

      // Return backdrop after the drawer
      if (backdrop) {
        menuDrawer.after(backdrop);
      }

      menuDrawer.style.pointerEvents = '';
      if (backdrop) backdrop.style.pointerEvents = '';
    }

    this.#destroyPortal();
  }

  /**
   * Remove the portal container from the DOM
   */
  #destroyPortal() {
    if (this.#portalContainer) {
      if (this.#portalEventHandler) {
        this.#portalContainer.removeEventListener('click', this.#portalEventHandler);
        this.#portalEventHandler = null;
      }
      this.#portalContainer.removeEventListener('keyup', this.#onKeyUp);
    }
    if (this.#portalContainer?.parentElement) {
      this.#portalContainer.remove();
    }
    this.#originalParent = null;
    this.#originalNextSibling = null;
    this.#menuDrawerEl = null;
    this.#backdropEl = null;
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
